import { useVoteAuditLog } from '../hooks/useVoteAuditLog';

/**
 * Vote Audit Log Component
 * Displays vote change history (admin-only)
 */
export function VoteAuditLog({ proposalId }) {
  const { auditLog, loading, error, getTimeAgo } = useVoteAuditLog(proposalId);

  // Check if user is admin
  const isAdmin = window.t24Governance?.canCreate || false;

  if (!isAdmin) {
    return (
      <div className="t24-vote-audit-log">
        <div className="audit-log-restricted">
          <span className="restricted-icon">🔒</span>
          <p>Vote audit log is only available to administrators.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="t24-vote-audit-log loading">
        <h3>Vote Change History</h3>
        <div className="t24-loading-spinner"></div>
        <p className="loading-text">Loading audit log...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="t24-vote-audit-log error">
        <h3>Vote Change History</h3>
        <div className="audit-log-error">
          <span className="error-icon">⚠️</span>
          <p>Failed to load audit log: {error}</p>
        </div>
      </div>
    );
  }

  if (!auditLog || auditLog.length === 0) {
    return (
      <div className="t24-vote-audit-log empty">
        <h3>
          <span className="audit-icon">🔍</span>
          Vote Change History
        </h3>
        <div className="audit-log-empty">
          <span className="empty-icon">✅</span>
          <p>No vote changes recorded. All votes were cast once and remained unchanged.</p>
        </div>
      </div>
    );
  }

  // Get vote type class
  const getVoteClass = (voteType) => {
    return `vote-${voteType}`;
  };

  // Get vote type label
  const getVoteLabel = (voteType) => {
    const labels = {
      'for': 'For',
      'against': 'Against',
      'abstain': 'Abstain'
    };
    return labels[voteType] || voteType;
  };

  return (
    <div className="t24-vote-audit-log">
      <h3>
        <span className="audit-icon">🔍</span>
        Vote Change History
      </h3>

      <div className="audit-log-description">
        <p>This audit log shows all vote changes for transparency. Each entry includes the voter, original vote, new vote, and timestamp.</p>
      </div>

      <div className="audit-log-entries">
        {auditLog.map((entry, index) => (
          <div key={entry.id || index} className="audit-entry">
            <div className="audit-entry-header">
              <span className="audit-voter">
                <span className="voter-icon">👤</span>
                {entry.display_name || 'Unknown User'}
              </span>
              <span className="audit-time">{getTimeAgo(entry.changed_at)}</span>
            </div>

            <div className="audit-entry-change">
              <span className="change-label">Changed vote from</span>
              <span className={`vote-badge ${getVoteClass(entry.old_vote_type)}`}>
                {getVoteLabel(entry.old_vote_type)}
              </span>
              <span className="change-arrow">→</span>
              <span className={`vote-badge ${getVoteClass(entry.new_vote_type)}`}>
                {getVoteLabel(entry.new_vote_type)}
              </span>
            </div>

            {entry.old_vote_weight !== entry.new_vote_weight && (
              <div className="audit-entry-weight">
                <span className="weight-label">Voting power:</span>
                <span className="weight-value">
                  {parseFloat(entry.old_vote_weight).toFixed(1)} → {parseFloat(entry.new_vote_weight).toFixed(1)}
                </span>
              </div>
            )}

            {entry.ip_address && entry.ip_address !== '0.0.0.0' && (
              <div className="audit-entry-meta">
                <span className="meta-icon">🌐</span>
                <span className="meta-ip">{entry.ip_address}</span>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="audit-log-footer">
        <p className="footer-note">
          <span className="note-icon">ℹ️</span>
          Total changes: {auditLog.length}
        </p>
      </div>
    </div>
  );
}
